/**
 * Manejo de errores para altas, ediciones, bajas y carga en listados del dashboard (Firestore).
 *
 * Uso en *List.jsx:
 *   import { DASHBOARD_CRUD, notifyDashboardCrudError, runDashboardCrud } from '../utils/dashboardCrud';
 *
 *   } catch (error) {
 *     notifyDashboardCrudError({ operation: DASHBOARD_CRUD.UPDATE, entityName: TITLE, error });
 *     throw error;
 *   }
 *
 *   await runDashboardCrud(DASHBOARD_CRUD.CREATE, TITLE, () => addDoc(...));
 *
 * Firestore sin propagar excepciones (react-edit-list):
 *   if (!(await handleDashboardCrud(DASHBOARD_CRUD.UPDATE, TITLE, () => setDoc(...)))) return false;
 *   const docRef = await handleDashboardCrudResult(DASHBOARD_CRUD.CREATE, TITLE, () => addDoc(...));
 *   if (!docRef) return false;
 *
 * Validación (react-edit-list devuelve false sin alert por defecto):
 *   if (isBlank(item.name)) {
 *     return failDashboardValidation({ operation: DASHBOARD_CRUD.CREATE, entityName: TITLE, missingFields: ['Nombre'] });
 *   }
 */

export const DASHBOARD_CRUD = {
  LOAD: 'load',
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
  REORDER: 'reorder',
};

const OPERATION_VERBS = {
  [DASHBOARD_CRUD.LOAD]: 'cargar',
  [DASHBOARD_CRUD.CREATE]: 'crear',
  [DASHBOARD_CRUD.UPDATE]: 'actualizar',
  [DASHBOARD_CRUD.DELETE]: 'eliminar',
  [DASHBOARD_CRUD.REORDER]: 'reordenar',
};

/** Mensajes en español alineados con códigos de Firebase/Firestore */
const FIRESTORE_CODE_HINTS = {
  'permission-denied': 'No tienes permiso para esta operación (revisa reglas de Firestore o inicio de sesión).',
  'not-found': 'El documento o la colección no existe.',
  'already-exists': 'Ya existe un documento con ese identificador.',
  'failed-precondition': 'No se cumplió una condición previa (p. ej. falta un índice compuesto en Firestore).',
  'invalid-argument': 'Los datos enviados no son válidos para Firestore.',
  unauthenticated: 'Debes iniciar sesión para realizar esta operación.',
  unavailable: 'Firestore no está disponible en este momento; intenta de nuevo.',
  'resource-exhausted': 'Se alcanzó un límite de cuota o de peticiones.',
  cancelled: 'La operación fue cancelada.',
  'deadline-exceeded': 'La operación tardó demasiado (timeout).',
  internal: 'Error interno en el servidor de Firebase.',
  unknown: 'Error no clasificado en Firebase.',
  aborted: 'La operación fue abortada.',
  'data-loss': 'Posible pérdida o corrupción de datos.',
  'out-of-range': 'Un valor está fuera del rango permitido.',
  unimplemented: 'Esta operación no está implementada en el backend.',
};

function normalizeError(error) {
  if (!error) {
    return { code: '', message: 'No se recibió detalle del error.' };
  }
  const code =
    (typeof error.code === 'string' && error.code) ||
    (typeof error.name === 'string' && error.name) ||
    '';
  const message =
    (typeof error.message === 'string' && error.message.trim()) ||
    String(error);
  return { code, message };
}

/**
 * Texto listo para mostrar al usuario (alert, toast, etc.).
 */
export function formatDashboardCrudError({ operation, entityName, error, context }) {
  const verb = OPERATION_VERBS[operation] || operation;
  const entity = entityName ? `"${entityName}"` : 'el registro';
  const { code, message } = normalizeError(error);
  const hint = code ? FIRESTORE_CODE_HINTS[code] : null;

  const parts = [`No se pudo ${verb} ${entity}.`];
  if (hint) {
    parts.push(hint);
  }
  if (code) {
    parts.push(`Código: ${code}.`);
  }
  if (message) {
    parts.push(`Mensaje: ${message}`);
  }
  if (context) {
    parts.push(context);
  }
  return parts.join('\n');
}

/** true si el valor está vacío (null, undefined, string en blanco). */
export function isBlank(value) {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  return false;
}

/**
 * Alerta por datos incompletos (validación antes de guardar en Firestore).
 */
export function notifyDashboardValidationError({ operation, entityName, message, missingFields }) {
  const verb = OPERATION_VERBS[operation] || operation;
  const entity = entityName ? `"${entityName}"` : 'el registro';
  const parts = [`No se pudo ${verb} ${entity}.`];
  if (message) {
    parts.push(message);
  } else if (missingFields?.length) {
    parts.push(`Completa: ${missingFields.join(', ')}.`);
  } else {
    parts.push('Faltan datos obligatorios.');
  }
  const text = parts.join('\n');
  console.warn('[Dashboard CRUD validation]', { operation, entityName, message, missingFields });
  if (typeof window !== 'undefined' && typeof window.alert === 'function') {
    window.alert(text);
  }
  return text;
}

/** Muestra alert de validación y devuelve false (para onInsert/onUpdate de react-edit-list). */
export function failDashboardValidation(options) {
  notifyDashboardValidationError(options);
  return false;
}

/**
 * Registra en consola y muestra alert con el detalle del error.
 */
export function notifyDashboardCrudError({ operation, entityName, error, context }) {
  const text = formatDashboardCrudError({ operation, entityName, error, context });
  console.error('[Dashboard CRUD]', { operation, entityName, error });
  if (typeof window !== 'undefined' && typeof window.alert === 'function') {
    window.alert(text);
  }
  return text;
}

/**
 * Ejecuta una operación async; en error muestra alert y devuelve false (no lanza).
 * Para onInsert / onUpdate / onDelete de react-edit-list.
 */
export async function handleDashboardCrud(operation, entityName, fn, context) {
  try {
    await fn();
    return true;
  } catch (error) {
    notifyDashboardCrudError({ operation, entityName, error, context });
    return false;
  }
}

/**
 * Igual que handleDashboardCrud pero devuelve el resultado de fn() o null si falló.
 */
export async function handleDashboardCrudResult(operation, entityName, fn, context) {
  try {
    return await fn();
  } catch (error) {
    notifyDashboardCrudError({ operation, entityName, error, context });
    return null;
  }
}

/**
 * Ejecuta una operación async; en error notifica. Por defecto no relanza la excepción.
 */
export async function runDashboardCrud(operation, entityName, fn, context, options = {}) {
  const { rethrow = false } = options;
  try {
    return await fn();
  } catch (error) {
    notifyDashboardCrudError({ operation, entityName, error, context });
    if (rethrow) {
      throw error;
    }
    return null;
  }
}
